"use client";

import { Controller } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { BackendInputWithLabel } from "../../backend-input";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import type { BackendInputWithLabelProps } from "../../backend-input";

/**
 * Mileage input component with German number formatting using react-number-format
 * - Thousand separator: dot (.)
 * - No decimal places (whole numbers only)
 * - Suffix: km
 * - No negative values allowed
 */
export function ControlledMileageInput<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  onFieldBlur,
  unit,
  ...props
}: Omit<
  BackendInputWithLabelProps,
  | "value"
  | "onChange"
  | "onBlur"
  | "error"
  | "name"
  | "defaultValue"
  | "type"
  | "step"
  | "min"
  | "max"
> & {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  onFieldBlur?: (fieldName: string) => void;
  unit?: string;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <NumericFormat
          customInput={BackendInputWithLabel}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={0}
          allowNegative={false}
          valueIsNumericString
          value={field.value}
          onValueChange={(values) => {
            field.onChange(values.floatValue ?? null);
          }}
          onBlur={() => {
            field.onBlur();
            onFieldBlur?.(name);
          }}
          error={error?.message}
          unit={unit || "km"}
          readOnly={props.readOnly}
          {...props}
        />
      )}
    />
  );
}
